import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../parts/SvgIcon";
import {
  getRecipe,
  getRecipesMap,
  storeRecipes,
} from "../../../../utils/external-food-cache";
import { externalFoodErrorKey } from "../../../../utils/external-food-errors";
import ExternalFoodDetailModal from "./ExternalFoodDetailModal";
import ExternalFoodImage from "./ExternalFoodImage";
import { buildExternalFoodMealTypeItems } from "./external-food-meal-types";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;
const MAX_SEARCH_OFFSET = 900;

const ATTRIBUTE_FLAGS = ["vegan", "vegetarian", "glutenFree", "dairyFree"];

function emptyFilters() {
  return {
    cuisine: [],
    diet: [],
    intolerances: [],
  };
}

function cloneFilters(filters) {
  return {
    cuisine: [...(filters.cuisine ?? [])],
    diet: [...(filters.diet ?? [])],
    intolerances: [...(filters.intolerances ?? [])],
  };
}

function withoutId(set, id) {
  const next = new Set(set);
  next.delete(id);
  return next;
}

function labelize(value) {
  return String(value)
    .split(/([\s-]+)/)
    .map((part) =>
      /^[\s-]+$/.test(part)
        ? part
        : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join("");
}

function formatNutrition(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0";
  return Number.isInteger(number)
    ? String(number)
    : number.toFixed(1).replace(/\.0$/, "");
}

function calculateCaloriesFromNutrients(nutrients = {}) {
  const protein = Number(nutrients.proteins);
  const carbs = Number(nutrients.carbs);
  const fats = Number(nutrients.fats);
  const macroValues = [protein, carbs, fats];

  if (!macroValues.some(Number.isFinite)) return null;

  return macroValues.reduce(
    (calories, value, index) =>
      calories + (Number.isFinite(value) ? value : 0) * [4, 4, 9][index],
    0
  );
}

function getRecipeCalories(recipe, nutrients) {
  const rawKcal = recipe?.kcal;
  const kcal = Number(rawKcal);
  const hasKcal =
    rawKcal !== null &&
    rawKcal !== undefined &&
    String(rawKcal).trim() !== "" &&
    Number.isFinite(kcal);

  return hasKcal ? kcal : calculateCaloriesFromNutrients(nutrients);
}

function hasActiveFilters(filters) {
  return Object.values(filters).some(
    (value) => Array.isArray(value) && value.length > 0
  );
}

function appendFilters(params, filters) {
  for (const [key, value] of Object.entries(filters)) {
    if (Array.isArray(value) && value.length) {
      params.set(key, value.join(","));
    }
  }
}

let filtersCache = null;

const MasterExternalFoodSection = ({
  search = "",
  initialRecipeId = null,
  onSearchChange,
  onClearSearch,
  onOpenCustom,
}) => {
  const { t } = useTranslation("coaching-portal");
  const ns = "masterFoodLibraryScreen.external";
  const filtersNs = `${ns}.filters`;
  const mealTypesNs = `${ns}.mealTypes`;
  const sortNs = `${ns}.sort`;

  const [query, setQuery] = useState({
    tab: "favorites",
    term: "",
    page: 1,
  });
  const { tab, page } = query;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState(null);

  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [pendingIds, setPendingIds] = useState(new Set());
  const [recipesById, setRecipesById] = useState(() => getRecipesMap());
  const randomCacheRef = useRef(null);

  const [filterOptions, setFilterOptions] = useState(() => filtersCache);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [mealType, setMealType] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersWrapRef = useRef(null);

  const [sort, setSort] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [sortOpen, setSortOpen] = useState(false);
  const sortWrapRef = useRef(null);
  const [detailId, setDetailId] = useState(() => {
    if (initialRecipeId != null && initialRecipeId !== "") {
      return String(initialRecipeId);
    }
    if (typeof window !== "undefined") {
      try {
        const params = new URLSearchParams(window.location.search);
        return (
          params.get("recipeId") ||
          params.get("recipe") ||
          params.get("detailId") ||
          null
        );
      } catch {
        return null;
      }
    }
    return null;
  });

  const handleOpenDetail = useCallback((id) => {
    setDetailId(id);
    if (typeof window !== "undefined" && window.history?.replaceState) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set("recipeId", String(id));
        window.history.replaceState({}, "", url.toString());
      } catch {
        // Ignore
      }
    }
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailId(null);
    if (typeof window !== "undefined" && window.history?.replaceState) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("recipeId");
        url.searchParams.delete("recipe");
        url.searchParams.delete("detailId");
        window.history.replaceState({}, "", url.toString());
      } catch {
        // Ignore
      }
    }
  }, []);

  const dropdownFiltersActive = hasActiveFilters(appliedFilters);
  const selectedFilterCount = Object.values(appliedFilters).reduce(
    (count, values) => count + values.length,
    0
  );
  const filtersActive = dropdownFiltersActive || Boolean(mealType);
  const sortActive = Boolean(sort);

  const switchTab = useCallback(
    (nextTab) => {
      if (nextTab === tab) return;
      onClearSearch?.();
      setFiltersOpen(false);
      setSortOpen(false);
      const cleared = emptyFilters();
      setDraftFilters(cleared);
      setAppliedFilters(cleared);
      setMealType("");
      setSort("");
      setSortDirection("desc");
      setQuery({ tab: nextTab, term: "", page: 1 });
    },
    [tab, onClearSearch]
  );

  const goToPage = useCallback(
    (next) => setQuery((prev) => ({ ...prev, page: next })),
    []
  );

  const patchDraftFilter = useCallback((key, value) => {
    setDraftFilters((prev) => {
      const current = prev[key] ?? [];
      const next = current.includes(value)
        ? current.filter((entry) => entry !== value)
        : [...current, value];
      return { ...prev, [key]: next };
    });
  }, []);

  const closeFilters = useCallback(() => {
    setAppliedFilters(cloneFilters(draftFilters));
    setFiltersOpen(false);
    setQuery((prev) => ({ ...prev, page: 1 }));
  }, [draftFilters]);

  const clearAllFilters = useCallback(() => {
    const cleared = emptyFilters();
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
    setFiltersOpen(false);
    setQuery((prev) => ({ ...prev, page: 1 }));
  }, []);

  const openFilters = useCallback(() => {
    if (filtersOpen) {
      closeFilters();
      return;
    }
    setDraftFilters(cloneFilters(appliedFilters));
    setSortOpen(false);
    setFiltersOpen(true);
  }, [appliedFilters, closeFilters, filtersOpen]);

  const selectMealType = useCallback((value, event) => {
    setMealType(value);
    setQuery((prev) => ({ ...prev, page: 1 }));
    event?.currentTarget?.scrollIntoView?.({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, []);

  const openSort = useCallback(() => {
    setFiltersOpen(false);
    setSortOpen((open) => !open);
  }, []);

  const selectSort = useCallback((value) => {
    setSort(value);
    setSortOpen(false);
    setQuery((prev) => ({ ...prev, page: 1 }));
  }, []);

  const toggleSortDirection = useCallback(() => {
    setSortDirection((prev) => {
      const options = filterOptions?.sortDirections?.length
        ? filterOptions.sortDirections
        : ["asc", "desc"];
      const index = options.indexOf(prev);
      return options[(index + 1) % options.length];
    });
    setQuery((prev) => ({ ...prev, page: 1 }));
  }, [filterOptions]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery((prev) =>
        prev.term === search ? prev : { ...prev, term: search, page: 1 }
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Load filter vocab once (module cache first; otherwise backend).
  useEffect(() => {
    if (filtersCache) {
      setFilterOptions(filtersCache);
      return undefined;
    }

    let cancelled = false;

    fetch("/actions/external-food/filters", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (cancelled || !payload) return;
        const filters = payload?.data?.filters ?? null;
        if (!filters) return;
        filtersCache = filters;
        setFilterOptions(filters);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!filtersOpen && !sortOpen) return undefined;

    const onPointerDown = (event) => {
      const inFilters = filtersWrapRef.current?.contains(event.target);
      const inSort = sortWrapRef.current?.contains(event.target);
      if (!inFilters && filtersOpen) closeFilters();
      if (!inSort) setSortOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        if (filtersOpen) closeFilters();
        setSortOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [closeFilters, filtersOpen, sortOpen]);

  useEffect(() => {
    const term = query.term.trim();
    const useRandom =
      tab === "general" && !term && !filtersActive && !sortActive;

    if (useRandom && randomCacheRef.current) {
      const cached = randomCacheRef.current;
      setItems(cached.items);
      setTotal(cached.total);
      setErrorKey(
        cached.errorKey === "rateLimit" ? "quotaExceeded" : cached.errorKey
      );
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    const params = new URLSearchParams({
      page: String(page),
      limit: String(PAGE_SIZE),
    });
    if (term) params.set("q", term);
    if (hasActiveFilters(appliedFilters)) {
      appendFilters(params, appliedFilters);
    }
    if (mealType) {
      params.set("type", mealType);
    }
    if (sortActive) {
      params.set("sort", sort);
      if (sortDirection) params.set("sortDirection", sortDirection);
    }

    const endpoint =
      tab === "favorites"
        ? "favorites"
        : term || filtersActive || sortActive
          ? "search"
          : "random";

    setLoading(true);
    setErrorKey(null);

    const queryString = params.toString().replace(/\+/g, "%20");

    fetch(`/actions/external-food/${endpoint}?${queryString}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (!res.ok) {
          const nextError = externalFoodErrorKey(res.status, payload);
          setItems([]);
          setTotal(0);
          setErrorKey(nextError);
          if (useRandom) {
            randomCacheRef.current = {
              items: [],
              total: 0,
              errorKey: nextError,
            };
          }
          return;
        }

        const recipes = payload?.data?.recipes ?? [];
        const nextTotal = Number(payload?.metadata?.total) || 0;
        storeRecipes(recipes);
        setRecipesById((prev) => {
          const next = { ...prev };
          for (const row of recipes) {
            const key = String(row.id ?? row.sourceId);
            if (key) next[key] = row;
          }
          return next;
        });
        setItems(recipes);
        setTotal(nextTotal);
        if (useRandom) {
          randomCacheRef.current = {
            items: recipes,
            total: nextTotal,
            errorKey: null,
          };
        }
      })
      .catch((err) => {
        if (cancelled || err?.name === "AbortError") return;
        setItems([]);
        setTotal(0);
        setErrorKey("generic");
        if (useRandom) {
          randomCacheRef.current = {
            items: [],
            total: 0,
            errorKey: "generic",
          };
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    tab,
    query.term,
    page,
    appliedFilters,
    mealType,
    filtersActive,
    sort,
    sortDirection,
    sortActive,
  ]);

  useEffect(() => {
    let cancelled = false;

    fetch("/actions/external-food/favorite-ids", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (cancelled || !payload) return;
        const ids = new Set((payload?.data?.ids ?? []).map(String));
        setFavoriteIds(ids);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const toggleFavorite = useCallback(
    async (item) => {
      const id = String(item.id ?? item.sourceId);
      const isFavorite = favoriteIds.has(id);

      setPendingIds((prev) => new Set(prev).add(id));
      try {
        const fullRecipe = getRecipe(id) || recipesById[id] || item;
        const res = await fetch(
          `/actions/external-food/favorites/${encodeURIComponent(id)}`,
          {
            method: isFavorite ? "DELETE" : "POST",
            credentials: "same-origin",
            headers: {
              Accept: "application/json",
              ...(isFavorite ? {} : { "Content-Type": "application/json" }),
            },
            body: isFavorite ? undefined : JSON.stringify(fullRecipe),
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        setFavoriteIds((prev) => {
          const next = isFavorite ? withoutId(prev, id) : new Set(prev).add(id);
          return next;
        });

        if (isFavorite && tab === "favorites") {
          setItems((prev) =>
            prev.filter((row) => String(row.id ?? row.sourceId) !== id)
          );
          setTotal((prev) => Math.max(0, prev - 1));
        } else if (!isFavorite && fullRecipe) {
          storeRecipes([fullRecipe]);
          setRecipesById((prev) => ({ ...prev, [id]: fullRecipe }));
        }
      } catch {
        setErrorKey("favorite");
      } finally {
        setPendingIds((prev) => withoutId(prev, id));
      }
    },
    [favoriteIds, tab, recipesById]
  );

  const maxPage = useMemo(() => {
    const term = query.term.trim();
    if (tab === "general" && !term && !filtersActive && !sortActive) return 1;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (tab !== "general") return pages;
    return Math.min(pages, Math.floor(MAX_SEARCH_OFFSET / PAGE_SIZE) + 1);
  }, [total, tab, query.term, filtersActive, sortActive]);

  const emptyNs =
    tab === "general"
      ? `${ns}.empty`
      : query.term.trim() || mealType
        ? `${ns}.emptyFavoritesSearch`
        : `${ns}.emptyFavorites`;
  const filterFields = useMemo(
    () => [
      {
        key: "cuisine",
        label: t(`${filtersNs}.cuisine`),
        options: filterOptions?.cuisines ?? [],
      },
      {
        key: "diet",
        label: t(`${filtersNs}.diet`),
        options: filterOptions?.diets ?? [],
      },
      {
        key: "intolerances",
        label: t(`${filtersNs}.intolerances`),
        options: filterOptions?.intolerances ?? [],
      },
    ],
    [filterOptions, filtersNs, t]
  );

  const mealTypeItems = useMemo(
    () => buildExternalFoodMealTypeItems(filterOptions?.mealTypes),
    [filterOptions?.mealTypes]
  );

  const sortOptions = filterOptions?.sort ?? [];
  const sortDirectionOptions = filterOptions?.sortDirections ?? ["asc", "desc"];
  const sortLabel = sort ? labelize(sort) : t(`${sortNs}.button`);

  return (
    <>
      <div className="external-food-control-panel">
        <div className="external-food-bar">
          <div className="external-food-tabs">
            <button
              type="button"
              data-button-type={tab === "favorites" ? "outline" : "secondary"}
              onClick={() => switchTab("favorites")}
            >
              <span className="external-food-tab-label">
                {t(`${ns}.tabs.favorites`)}
              </span>
              {favoriteIds.size > 0 && (
                <span className="external-food-favorite-count">
                  {favoriteIds.size}
                </span>
              )}
            </button>
            <button
              type="button"
              data-button-type={tab === "general" ? "outline" : "secondary"}
              onClick={() => switchTab("general")}
            >
              <span className="external-food-tab-label">
                {t(`${ns}.tabs.general`)}
              </span>
            </button>
          </div>

          <input
            className="external-food-search"
            type="search"
            name="external-food-search"
            placeholder="Search"
            value={search}
            onChange={(event) => onSearchChange?.(event.target.value)}
            autoComplete="off"
          />

          <div className="external-food-controls">
            <div className="external-food-filters" ref={filtersWrapRef}>
              {dropdownFiltersActive && (
                <button
                  type="button"
                  className="external-food-clear-filters"
                  onClick={clearAllFilters}
                >
                  {t(`${filtersNs}.clearAll`)}
                </button>
              )}

              <button
                type="button"
                data-button-type={
                  dropdownFiltersActive ? "outline" : "secondary"
                }
                className={
                  filtersOpen
                    ? "external-food-filters-trigger is-open"
                    : "external-food-filters-trigger"
                }
                aria-expanded={filtersOpen}
                aria-haspopup="dialog"
                onClick={openFilters}
              >
                <span>
                  {t(`${filtersNs}.button`)}
                  {selectedFilterCount > 0 ? ` (${selectedFilterCount})` : ""}
                </span>
                <SvgIcon src="/img/new-icons/chevron-down.svg" />
              </button>

              {filtersOpen && (
                <div
                  className="external-food-filters-panel"
                  role="dialog"
                  aria-label={t(`${filtersNs}.button`)}
                >
                  <div className="external-food-filters-fields">
                    {filterFields.map(({ key, label, options }) => {
                      const selected = draftFilters[key] ?? [];
                      return (
                        <div key={key} className="external-food-filter-field">
                          <p className="external-food-filter-field-label">
                            {label}
                            {selected.length > 0 ? (
                              <span>({selected.length})</span>
                            ) : null}
                          </p>
                          <ul
                            className="external-food-filter-menu"
                            role="group"
                            aria-label={label}
                          >
                            {options.map((value) => {
                              const checked = selected.includes(value);
                              const optionId = `external-food-filter-${key}-${String(
                                value
                              ).replace(/\s+/g, "-")}`;
                              return (
                                <li key={value} role="none">
                                  <label
                                    htmlFor={optionId}
                                    style={{ position: "relative" }}
                                    className={
                                      checked
                                        ? "external-food-filter-option is-checked"
                                        : "external-food-filter-option"
                                    }
                                    role="menuitemcheckbox"
                                    aria-checked={checked}
                                  >
                                    <input
                                      id={optionId}
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() =>
                                        patchDraftFilter(key, value)
                                      }
                                    />
                                    <span
                                      className="external-food-filter-checkbox"
                                      aria-hidden="true"
                                    />
                                    <span>{labelize(value)}</span>
                                  </label>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="external-food-sort" ref={sortWrapRef}>
              <button
                type="button"
                data-button-type={sortActive ? "outline" : "secondary"}
                className={
                  sortOpen
                    ? "external-food-filters-trigger is-open"
                    : "external-food-filters-trigger"
                }
                aria-expanded={sortOpen}
                aria-haspopup="listbox"
                disabled={!sortOptions.length}
                onClick={openSort}
              >
                <span>{sortLabel}</span>
                <SvgIcon src="/img/new-icons/chevron-down.svg" />
              </button>

              {sortActive && sortDirectionOptions.length > 0 && (
                <button
                  type="button"
                  data-button-type="secondary"
                  data-icon-only
                  className={
                    sortDirection === "asc"
                      ? "external-food-sort-direction is-asc"
                      : "external-food-sort-direction"
                  }
                  aria-label={t(`${sortNs}.directions.${sortDirection}`, {
                    defaultValue: labelize(sortDirection),
                  })}
                  title={t(`${sortNs}.directions.${sortDirection}`, {
                    defaultValue: labelize(sortDirection),
                  })}
                  onClick={toggleSortDirection}
                >
                  <SvgIcon src="/img/new-icons/arrow-down.svg" />
                </button>
              )}

              {sortOpen && (
                <div
                  className="external-food-sort-panel"
                  role="listbox"
                  aria-label={t(`${sortNs}.button`)}
                >
                  <div className="external-food-sort-options">
                    <button
                      type="button"
                      role="option"
                      aria-selected={!sort}
                      className={
                        !sort
                          ? "external-food-sort-option is-selected"
                          : "external-food-sort-option"
                      }
                      onClick={() => selectSort("")}
                    >
                      <span
                        className="external-food-sort-radio"
                        aria-hidden="true"
                      />
                      {t(`${sortNs}.default`)}
                    </button>
                    {sortOptions.map((value) => {
                      const selected = sort === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          className={
                            selected
                              ? "external-food-sort-option is-selected"
                              : "external-food-sort-option"
                          }
                          onClick={() => selectSort(value)}
                        >
                          <span
                            className="external-food-sort-radio"
                            aria-hidden="true"
                          />
                          {labelize(value)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {tab === "favorites" && favoriteIds.size > 0 && (
              <button
                type="button"
                data-button-type="secondary"
                className="external-food-favorite-more-button"
                onClick={() => switchTab("general")}
              >
                <img
                  className="external-food-favorite-more-icon"
                  src="/img/new-icons/heart-outline.svg"
                  alt=""
                />
                {t(`${ns}.favoriteMoreRecipes`)}
              </button>
            )}

            <button
              type="button"
              data-button-type="primary"
              className="external-food-custom-button"
              onClick={onOpenCustom}
            >
              <img src="/img/new-icons/add.svg" alt="" />
              {t(`${ns}.custom`)}
            </button>
          </div>
        </div>
      </div>

      {!loading &&
        !errorKey &&
        mealTypeItems.length > 1 &&
        (tab !== "favorites" || favoriteIds.size > 0) && (
          <div
            className="external-food-meal-types"
            role="radiogroup"
            aria-label={t(`${filtersNs}.mealType`)}
          >
            <div className="external-food-meal-types-track">
              {mealTypeItems.map((item) => {
                const selected = mealType === item.value;
                const label = item.fallbackLabel
                  ? labelize(item.value)
                  : t(`${mealTypesNs}.${item.id}`, {
                      defaultValue: labelize(item.value || "all"),
                    });
                return (
                  <button
                    key={item.value || "all"}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={
                      selected
                        ? "external-food-meal-type is-selected"
                        : "external-food-meal-type"
                    }
                    onClick={(event) => selectMealType(item.value, event)}
                  >
                    <span
                      className="external-food-meal-type-label"
                      title={label}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

      <section
        className={
          !loading && !errorKey && items.length === 0
            ? "meal-table external-food-table external-food-empty-table"
            : "meal-table external-food-table"
        }
        data-scrollable="true"
        role="region"
        aria-label={t(`${ns}.title`, { defaultValue: "External food" })}
      >
        {loading && (
          <div className="external-food-status">{t(`${ns}.loading`)}</div>
        )}

        {!loading && errorKey && (
          <div className="empty-state">
            <p>{t(`${ns}.errors.title`)}</p>
            <span>{t(`${ns}.errors.${errorKey}`)}</span>
          </div>
        )}

        {!loading && !errorKey && items.length === 0 && (
          <div
            className={
              tab === "favorites" && !query.term.trim() && !mealType
                ? "empty-state external-food-empty-state"
                : "empty-state"
            }
          >
            <p>{t(`${emptyNs}.title`)}</p>
            <span>{t(`${emptyNs}.description`)}</span>
            {tab === "favorites" && !query.term.trim() && !mealType && (
              <button
                type="button"
                data-button-type="primary"
                onClick={() => switchTab("general")}
              >
                {t(`${ns}.emptyFavorites.explore`)}
              </button>
            )}
          </div>
        )}

        {!loading && !errorKey && items.length > 0 && (
          <div className="external-food-grid">
            {items.map((item) => {
              const id = String(item.id);
              const isFavorite = favoriteIds.has(id);
              const isPending = pendingIds.has(id);
              const attributes = ATTRIBUTE_FLAGS.filter(
                (flag) => item[flag]
              ).map((flag) => t(`${ns}.attributes.${flag}`));
              const cuisines = (item.cuisines ?? []).map(labelize).join(", ");
              const nutrients =
                tab === "favorites"
                  ? (item.recipe?.nutrients ?? {})
                  : (item.nutrients ?? {});
              const nutrition = [
                ["P", nutrients.proteins, "protein"],
                ["C", nutrients.carbs, "carbs"],
                ["F", nutrients.fibers, "fiber"],
                [
                  "K",
                  getRecipeCalories(
                    tab === "favorites" ? item.recipe : item,
                    nutrients
                  ),
                  "calories",
                ],
              ];

              return (
                <article
                  key={id}
                  className="external-food-card"
                  onClick={() => handleOpenDetail(item.id)}
                >
                  <div className="external-food-card-image-wrap">
                    <ExternalFoodImage
                      className="external-food-card-image"
                      image={item.image}
                      imageType={item.imageType}
                      alt=""
                      loading="lazy"
                    />
                    <button
                      type="button"
                      className={
                        isFavorite
                          ? "external-food-card-favorite is-favorite"
                          : "external-food-card-favorite"
                      }
                      disabled={isPending}
                      aria-pressed={isFavorite}
                      aria-label={t(
                        isFavorite ? `${ns}.unfavorite` : `${ns}.favorite`
                      )}
                      title={t(
                        isFavorite ? `${ns}.unfavorite` : `${ns}.favorite`
                      )}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleFavorite(item);
                      }}
                    >
                      <SvgIcon
                        src={
                          isFavorite
                            ? "/img/new-icons/heart-solid.svg"
                            : "/img/new-icons/heart-outline.svg"
                        }
                        useCurrentColor
                        classNames="external-food-card-favorite-icon"
                      />
                    </button>
                  </div>
                  <div className="external-food-card-body">
                    <div className="external-food-card-top">
                      <h3 className="external-food-card-title">{item.title}</h3>
                      <p className="external-food-card-meta">
                        {item.readyInMinutes
                          ? t(`${ns}.minutes`, { count: item.readyInMinutes })
                          : "—"}
                        <span aria-hidden="true"> · </span>
                        {item.servings ? `${item.servings} servings` : "—"}
                        {cuisines ? (
                          <>
                            <span aria-hidden="true"> · </span>
                            {cuisines}
                          </>
                        ) : null}
                      </p>
                    </div>
                    <div className="external-food-card-bottom">
                      <div
                        className="external-food-card-nutrition"
                        aria-label="Nutrition"
                      >
                        {nutrition.map(([label, value, type]) => (
                          <span
                            key={label}
                            className={`external-food-nutrition-badge is-${type}`}
                          >
                            <strong>{label}</strong>{" "}
                            {formatNutrition(
                              label === "K" ? Math.round(Number(value)) : value
                            )}
                            {label === "K" ? "" : "g"}
                          </span>
                        ))}
                      </div>
                      <div className="external-food-card-dietary">
                        {attributes.map((label) => (
                          <span key={label}>{label}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
            {tab === "favorites" &&
              favoriteIds.size > 0 &&
              items.length < PAGE_SIZE &&
              items.length % 5 !== 0 && (
                <button
                  type="button"
                  className="external-food-favorite-more-card"
                  onClick={() => switchTab("general")}
                >
                  <SvgIcon
                    src="/img/new-icons/heart-outline.svg"
                    useCurrentColor
                    classNames="external-food-favorite-more-card-icon"
                  />
                  <strong>{t(`${ns}.favoriteMoreRecipes`)}</strong>
                  <span>
                    {t(`${ns}.favoriteMoreRecipesDescription`, {
                      defaultValue:
                        "Browse 365,000+ recipes and save the ones you assign.",
                    })}
                  </span>
                </button>
              )}
          </div>
        )}
      </section>

      {!errorKey && maxPage > 1 && (
        <div className="external-food-pagination">
          <button
            type="button"
            data-button-type="secondary"
            disabled={loading || page <= 1}
            onClick={() => goToPage(Math.max(1, page - 1))}
          >
            <SvgIcon
              src="/img/new-icons/arrow-left.svg"
              useCurrentColor
              style={{ width: "1.25rem", height: "1.25rem" }}
            />
            <span>{t(`${ns}.previous`)}</span>
          </button>
          <button
            type="button"
            data-button-type="secondary"
            disabled={loading || page >= maxPage}
            onClick={() => goToPage(Math.min(maxPage, page + 1))}
          >
            <span>{t(`${ns}.next`)}</span>
            <SvgIcon
              src="/img/new-icons/arrow-right.svg"
              useCurrentColor
              style={{ width: "1.25rem", height: "1.25rem" }}
            />
          </button>
        </div>
      )}

      {detailId != null && (
        <ExternalFoodDetailModal
          recipeId={detailId}
          initialRecipe={recipesById[String(detailId)] || getRecipe(detailId)}
          isFavorite={favoriteIds.has(String(detailId))}
          isPending={pendingIds.has(String(detailId))}
          onClose={handleCloseDetail}
          onToggleFavorite={toggleFavorite}
        />
      )}
    </>
  );
};

MasterExternalFoodSection.propTypes = {
  search: PropTypes.string,
  initialRecipeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSearchChange: PropTypes.func,
  onClearSearch: PropTypes.func,
  onOpenCustom: PropTypes.func,
};

export default MasterExternalFoodSection;
